import { apiRoutes, Hiver_Base_Url } from "../Constant/ApiRoutes";
import axios from "axios";
import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
} from "../apiRequest";
import { getState, STORAGE_KEYS } from "../Constant/LocalStorage";
import { TENANT_PLATFORM } from "@hiver/bifrost-js/lib";

// HOT (Omni) users authenticate via Heimdall and have no Laravel session,
// so PHP-only endpoints (hiverAxios) 401 for them. Use this guard to route
// affected calls to the Python backend (HOT-aware) for HOT users only.
const isHotTenant = () =>
  getState(STORAGE_KEYS.KB_ACTIVE_TENANT) === TENANT_PLATFORM.HIVERWEB;

// Axios instance for PHP/Laravel backend (Hiver main backend)
const hiverAxios = axios.create({
  withCredentials: true,
  baseURL: Hiver_Base_Url,
});

// Helper to get the current KB ID for scoping API calls in multi-KB mode
const getKbParams = () => {
  const kbId = window.__kbId;
  return kbId ? { knowledgeBaseId: kbId } : undefined;
};

//Get MailBox Contact Details
export const getMailBoxDetails = () => {
  return getRequest(apiRoutes.kbSharedMainBox, getKbParams());
};

//Get KB Info
export const getKbInfo = () => {
  return getRequest(apiRoutes.kbSettingsData, getKbParams());
};

// List all Knowledge Bases for the current user.
// HIG path: PHP /api/kbs (Laravel session auth + writes kb_links_all CORS
//           allowlist as a side effect — preserved by keeping HIG on PHP).
// HOT path: Python /v1/admin/list-kbs (Bifrost auth via getRequest helper).
// Both endpoints return the same shape: { data: { knowledgeBases: [...] } }
// with knowledgeBaseId, title, subdomain, link, logo, isInternal, ... .
export const listKnowledgeBases = () => {
  if (isHotTenant()) {
    return getRequest(apiRoutes.kbList);
  }
  return hiverAxios.get('api/kbs');
};

// Get the user's server-persisted default KB ID.
// Phase 1: HOT users have no Python equivalent yet — short-circuit to a
// shaped null response so SiteSettingsProvider falls through to the
// localStorage default at line ~336. Tracked as a Phase-2 follow-up.
export const getDefaultKbApi = () => {
  if (isHotTenant()) {
    return Promise.resolve({ data: { defaultKbId: null } });
  }
  return hiverAxios.get('api/kb-default');
};

// Save the user's default KB ID on the server.
// Phase 1: HOT users persist via localStorage only (handled at the call
// site — this is a no-op so the call signature stays uniform).
export const setDefaultKbApi = (knowledgeBaseId) => {
  if (isHotTenant()) {
    return Promise.resolve({ data: { defaultKbId: knowledgeBaseId } });
  }
  return hiverAxios.put('api/kb-default', { knowledgeBaseId });
};

// Get a one-time gateway auth code for cross-domain SSO (60s TTL)
export const getGatewayAuthCode = () => {
  return hiverAxios.get('api/auth/login/code');
};

//to fetch all available features
export const getAvailableFeatures = () => {
  return getRequest(apiRoutes.availableFeatures);
};

export const getLoggedInUserDetails = () => {
  return getRequest(apiRoutes.getUserDetails);
}

//Branding
export const postSiteBranding = (payload) => {
  return postRequest(apiRoutes.kbBranding, payload, getKbParams());
};

export const putSiteBranding = (payload) => {
  return putRequest(apiRoutes.kbBranding, payload, getKbParams());
};

// SiteSettings
export const putSiteSetting = (payload) => {
  return putRequest(apiRoutes.kbSiteSetting, payload, getKbParams());
};

//// Domain Check
export const domainCheck = (payload) => {
  return getRequest(apiRoutes.kbcheckSubdomain, { keyword: payload, ...getKbParams() });
};

//// Code Snippets
export const postSnippets = (payload) => {
  return postRequest(apiRoutes.kbSnippets, payload, getKbParams());
};

export const putSnippets = (payload) => {
  return putRequest(apiRoutes.kbSnippets, payload, getKbParams());
};

export const deleteSnippets = (payload) => {
  return deleteRequest(apiRoutes.kbSnippets, payload, getKbParams());
};
